export interface Question {
  id: number;
  type: 'MCQ' | 'T/F' | 'Essay' | 'Short';
  difficulty: 'easy' | 'medium' | 'hard';
  topic: string;
  points: number;
  question: string;
  options?: string[];
  correctAnswer?: string;
}

export interface Assessment {
  id: number;
  name: string;
  description: string;
  status: string;
  type: string;
  questions: number;
  time: string;
  passing: string;
  created: string;
}

export interface AvailableQuestion {
  id: number;
  type: string;
  difficulty: string;
  question: string;
  points: number;
}

export interface CarveAssessmentSource {
  id: number;
  name: string;
  questions: number;
}

export const getQuestions = async (): Promise<{
  success: boolean;
  data?: Question[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Question[] = [
      {
        id: 1,
        type: 'MCQ',
        difficulty: 'medium',
        topic: 'Leadership',
        points: 10,
        question: 'What is the primary purpose of leadership in an organization?',
        options: [
          'To control employees',
          'To inspire and guide teams toward goals',
          'To maximize profits',
          'To enforce rules'
        ],
        correctAnswer: 'To inspire and guide teams toward goals'
      },
      {
        id: 2,
        type: 'T/F',
        difficulty: 'easy',
        topic: 'Programming',
        points: 5,
        question: 'Python is a compiled language.',
        correctAnswer: 'False'
      },
      {
        id: 3,
        type: 'Essay',
        difficulty: 'hard',
        topic: 'Programming',
        points: 20,
        question: 'Explain the concept of object-oriented programming and its main principles.',
      },
      {
        id: 4,
        type: 'Short',
        difficulty: 'easy',
        topic: 'Technical',
        points: 5,
        question: 'What does API stand for?',
      },
      {
        id: 5,
        type: 'MCQ',
        difficulty: 'medium',
        topic: 'Project Management',
        points: 10,
        question: 'Which of the following is NOT a principle of Agile methodology?',
        options: [
          'Customer collaboration',
          'Working software',
          'Comprehensive documentation',
          'Responding to change'
        ],
        correctAnswer: 'Comprehensive documentation'
      }
    ];

    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Question[]>(ENDPOINTS.QUESTIONS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch questions' }
    };
  }
};

export const getAssessments = async (): Promise<{
  success: boolean;
  data?: Assessment[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Assessment[] = [
      {
        id: 1,
        name: 'Leadership Fundamentals Assessment',
        description: 'Test your understanding of leadership principles',
        status: 'active',
        type: 'Quiz',
        questions: 15,
        time: '45 min',
        passing: '70%',
        created: '2 days ago',
      },
      {
        id: 2,
        name: 'Python Programming Test',
        description: 'Comprehensive Python skills evaluation',
        status: 'active',
        type: 'Test',
        questions: 25,
        time: '90 min',
        passing: '80%',
        created: '1 week ago',
      },
      {
        id: 3,
        name: 'JavaScript ES6+ Quiz',
        description: 'Modern JavaScript features and syntax',
        status: 'active',
        type: 'Quiz',
        questions: 20,
        time: '30 min',
        passing: '75%',
        created: '5 days ago',
      },
      {
        id: 4,
        name: 'React Fundamentals Test',
        description: 'Comprehensive test covering React basics',
        status: 'active',
        type: 'Test',
        questions: 30,
        time: '75 min',
        passing: '80%',
        created: '2 weeks ago',
      },
    ];

    await new Promise(resolve => setTimeout(resolve, 600));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Assessment[]>(ENDPOINTS.ASSESSMENTS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch assessments' }
    };
  }
};

export const getAvailableQuestions = async (): Promise<{
  success: boolean;
  data?: AvailableQuestion[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: AvailableQuestion[] = [
      { id: 1, type: 'MCQ', difficulty: 'medium', question: 'What is the primary purpose of leadership in an organization?', points: 10 },
      { id: 2, type: 'T/F', difficulty: 'easy', question: 'Python is a compiled language.', points: 5 },
      { id: 3, type: 'Essay', difficulty: 'hard', question: 'Explain the concept of object-oriented programming and its main principles.', points: 20 },
      { id: 4, type: 'Short', difficulty: 'easy', question: 'What does API stand for?', points: 5 },
    ];

    await new Promise(resolve => setTimeout(resolve, 400));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<AvailableQuestion[]>(ENDPOINTS.AVAILABLE_QUESTIONS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch available questions' }
    };
  }
};

export const getCarveAssessmentSources = async (): Promise<{
  success: boolean;
  data?: CarveAssessmentSource[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: CarveAssessmentSource[] = [
      { id: 1, name: 'React Fundamentals Final Assessment', questions: 25 },
      { id: 2, name: 'JavaScript ES6+ Quiz', questions: 15 },
      { id: 3, name: 'Advanced React Patterns', questions: 30 },
    ];

    await new Promise(resolve => setTimeout(resolve, 450));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<CarveAssessmentSource[]>(ENDPOINTS.CARVE_ASSESSMENTS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch carve assessment sources' }
    };
  }
};
